import { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Building, Boxes } from 'lucide-react';

export const CaseStudiesSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);
  const dragOffset = useRef(0);
  const [dragDelta, setDragDelta] = useState(0);

  const caseStudies = [
    {
      icon: Building2,
      badge: 'Hyperscaler',
      location: 'Norddeutschland',
      title: 'Hyperscaler – Norddeutschland',
      image: 'https://customer-assets.emergentagent.com/job_462b1c9b-939c-4705-8ff2-b6f811719f70/artifacts/d642e8t3_Norddeutschland.png',
      description:
        'Ein führender Cloud-Anbieter integrierte 20 MWh BESS zur Netzstabilisierung.',
      results: [
        '28 % Reduktion der Spitzenlastkosten',
        'Vollständige Frequenzregelungsteilnahme',
      ],
      color: 'bg-primary',
      borderColor: 'border-primary/20',
    },
    {
      icon: Building,
      badge: 'Enterprise',
      location: 'Rhein-Main',
      title: 'Enterprise-Rechenzentrum – Rhein-Main',
      image: 'https://customer-assets.emergentagent.com/job_462b1c9b-939c-4705-8ff2-b6f811719f70/artifacts/p0l26f7j_RheinMain.png',
      description:
        'Migration von Diesel-USV zu BESS für ein Tier-III-Rechenzentrum.',
      results: [
        '99,999 % Verfügbarkeit erreicht',
        '45 % weniger CO₂-Emissionen',
      ],
      color: 'bg-success',
      borderColor: 'border-success/20',
    },
    {
      icon: Boxes,
      badge: 'Colocation',
      location: 'Bayern',
      title: 'Colocation-Anbieter – Bayern',
      image: 'https://customer-assets.emergentagent.com/job_462b1c9b-939c-4705-8ff2-b6f811719f70/artifacts/gf67zgrk_Bayern.png',
      description:
        'Integration von 5 MWh BESS mit bestehender PV-Anlage für maximale Eigenverbrauchsquote.',
      results: [
        '72 % Eigenverbrauch aus Solarenergie',
        'ROI innerhalb von 4 Jahren',
      ],
      color: 'bg-secondary',
      borderColor: 'border-secondary/20',
    },
  ];

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    dragOffset.current = 0;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    dragOffset.current = diff;
    setDragDelta(diff);
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    const threshold = 60;
    if (dragOffset.current < -threshold && activeSlide < caseStudies.length - 1) {
      setActiveSlide((prev) => prev + 1);
    } else if (dragOffset.current > threshold && activeSlide > 0) {
      setActiveSlide((prev) => prev - 1);
    }
    setDragDelta(0);
    dragOffset.current = 0;
  };

  const renderDesktopCard = (study, index) => (
    <Card
      key={index}
      className={`group hover:shadow-xl transition-all duration-300 border-2 ${study.borderColor} hover:-translate-y-2 flex flex-col`}
    >
      <CardContent className="p-0 flex flex-col h-full">
        <img src={study.image} alt={study.title} className="w-full aspect-video object-cover object-center rounded-t-lg" loading="lazy" />
        <div className="p-8 flex flex-col flex-1">
          <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <study.icon className="w-8 h-8 text-foreground" />
          </div>
          <Badge className={`${study.color} text-primary-foreground mb-4 w-fit`}>{study.badge} – {study.location}</Badge>
          <h3 className="font-heading font-semibold text-xl text-foreground mb-3">{study.title}</h3>
          <p className="text-muted-foreground mb-6 leading-relaxed">{study.description}</p>
          <div className="mt-auto space-y-2 pt-4 border-t border-border">
            <p className="text-sm font-medium text-foreground mb-2">Ergebnis:</p>
            {study.results.map((result, idx) => (
              <div key={idx} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderMobileCard = (study, index) => (
    <Card
      key={index}
      className={`border ${study.borderColor} flex flex-col shadow-sm h-full`}
    >
      <CardContent className="p-0 flex flex-col h-full">
        <img src={study.image} alt={study.title} className="w-full aspect-video object-cover object-center rounded-t-lg" loading="lazy" />
        <div className="p-6 flex flex-col flex-1">
          <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mb-3">
            <study.icon className="w-7 h-7 text-foreground" />
          </div>
          <Badge className={`${study.color} text-primary-foreground mb-3 w-fit`}>{study.badge} – {study.location}</Badge>
          <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{study.title}</h3>
          <p className="text-muted-foreground text-sm mb-5 leading-relaxed">{study.description}</p>
          <div className="mt-auto space-y-2 pt-4 border-t border-border">
            <p className="text-sm font-medium text-foreground mb-2">Ergebnis:</p>
            {study.results.map((result, idx) => (
              <div key={idx} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const translateX = -activeSlide * 100 + (dragDelta / (typeof window !== 'undefined' ? window.innerWidth : 390)) * 100;

  return (
    <section id="case-studies" className="py-12 sm:py-20 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-base sm:text-xl text-muted-foreground font-normal mb-2">
              Fallstudien:
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-primary">
              Erfolgreiche BESS-Anwendungen in Rechenzentren
            </h2>
          </div>

          {/* Desktop/Tablet: Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => renderDesktopCard(study, index))}
          </div>

          {/* Mobile: Carousel */}
          <div
            className="md:hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-testid="case-studies-carousel"
          >
            <div className="overflow-hidden">
              <div
                className="flex items-stretch"
                style={{
                  transform: `translateX(${translateX}%)`,
                  transition: dragDelta !== 0 ? 'none' : 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                }}
              >
                {caseStudies.map((study, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-2 flex">
                    <div className="w-full">{renderMobileCard(study, index)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2.5 mt-6">
              {caseStudies.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === activeSlide
                      ? 'bg-primary w-7'
                      : 'bg-border w-2.5'
                  }`}
                  data-testid={`case-study-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
